import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Heading, Paragraph, Tag } from '@digdir/designsystemet-react';
import type { PlanArea } from '../../../api/workEnvironmentTypes';
import {
  areaNames,
  areasByRisk,
  choiceId,
  choosableAreas,
  hasPlan,
  planAction,
  PLAN_STATUS_COLORS,
  plansOf,
  toggled,
} from '../planView';
import { areaName, LEVEL_COLORS, oneDecimal } from '../riskView';
import process from '../WorkEnvironmentSurveyProcess.module.css';
import measures from '../../WorkEnvironmentTeamMeasures/WorkEnvironmentTeamMeasures.module.css';
import classes from '../WorkEnvironmentSurveyRisk.module.css';

type Props = {
  areas: PlanArea[];
  canChange: boolean;
  /** The area the page was opened at, chosen for a new plan */
  focus?: string | null;
  onOpenPlan: (planId: number) => void;
  onCreate: (areaKeys: string[]) => void;
};

/**
 * The plans the team has, and the areas at risk still without one. One plan can deal with
 * several areas, as one measure often helps with more than one.
 */
export const AreaChoiceCard = ({ areas, canChange, focus = null, onOpenPlan, onCreate }: Props): ReactElement => {
  const { t, i18n } = useTranslation();
  const { atRisk, low } = areasByRisk(areas);
  const [showLow, setShowLow] = useState(false);
  const [chosen, setChosen] = useState<string[]>(focus ? [focus] : []);
  const plans = plansOf(areas);
  const choosable = choosableAreas(showLow ? [...atRisk, ...low] : atRisk);
  const selected = choosable.filter((area) => chosen.includes(area.key));

  return (
    <section aria-labelledby="plan-areas-heading" className={process.card}>
      {plans.length > 0 && (
        <div className={process.cardBody}>
          <Heading level={2} data-size="sm">
            {t('workEnvironmentPlan.plans.title')}
          </Heading>
          <ul className={classes.dataAreas}>
            {plans.map(({ plan, areas: covered }) => (
              <li key={plan.id} className={classes.dataArea}>
                <div className={classes.areaSummary}>
                  <span className={classes.areaTitle}>
                    <span className="ds-label">
                      {t('workEnvironmentPlan.plans.name', { names: areaNames(covered, t) })}
                    </span>
                    <span className={classes.areaTags}>
                      {covered.map((area) => (
                        <Tag key={area.key} data-size="sm" data-color={LEVEL_COLORS[area.level]}>
                          {t(`workEnvironmentRisk.matrix.levels.${area.level}`)}
                        </Tag>
                      ))}
                    </span>
                  </span>
                  <Tag data-size="sm" data-color={PLAN_STATUS_COLORS[plan.approvalState]}>
                    {t(`workEnvironmentPlan.status.${plan.approvalState}`)}
                  </Tag>
                </div>
                <div>
                  <Button variant="secondary" data-size="sm" onClick={() => onOpenPlan(plan.id)}>
                    {t(`workEnvironmentPlan.plans.${planAction(plan, canChange)}`)}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className={process.cardBody}>
        <div className={process.sideHeading}>
          <Heading level={2} data-size="sm" id="plan-areas-heading">
            {t('workEnvironmentPlan.choose.title')}
          </Heading>
          <Paragraph data-size="sm" className={process.subtle}>
            {t('workEnvironmentPlan.choose.intro')}
          </Paragraph>
        </div>
        {choosable.length === 0 ? (
          <Paragraph data-size="sm">{t('workEnvironmentPlan.choose.allPlanned')}</Paragraph>
        ) : (
          <ul className={measures.choices}>
            {choosable.map((area) => (
              <li key={area.key} id={choiceId(area.key)}>
                <label className={measures.choice}>
                  {canChange && (
                    <input
                      type="checkbox"
                      className="ds-input"
                      checked={chosen.includes(area.key)}
                      onChange={() => setChosen(toggled(chosen, area.key))}
                    />
                  )}
                  <span className={measures.choiceText}>
                    <span className="ds-label">{areaName(area, t)}</span>
                    <span className={measures.choiceMeta}>
                      <Tag data-size="sm" data-color={LEVEL_COLORS[area.level]}>
                        {t(`workEnvironmentRisk.matrix.levels.${area.level}`)}
                      </Tag>
                      {area.decision && (
                        <Tag data-size="sm" data-color="neutral">
                          {t('workEnvironmentPlan.choose.decided')}
                        </Tag>
                      )}
                    </span>
                    <span className={`ds-paragraph ${process.subtle}`} data-size="xs">
                      {area.proposals > 0
                        ? t('workEnvironmentPlan.choose.engagement', {
                            count: area.proposals,
                            highlights: t('workEnvironmentPlan.choose.highlights', { count: area.highlights }),
                          })
                        : t('workEnvironmentPlan.choose.noProposals')}
                    </span>
                    <span className={`ds-paragraph ${process.subtle}`} data-size="xs">
                      {t(`workEnvironmentPlan.choose.score.${area.dimension}`, {
                        value: oneDecimal(area.dimensionScore, i18n.language),
                      })}
                    </span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
        )}
        {low.length > 0 && (
          <div>
            <Button variant="tertiary" data-size="sm" onClick={() => setShowLow(!showLow)}>
              {showLow
                ? t('workEnvironmentPlan.choose.hideLow')
                : t('workEnvironmentPlan.choose.showLow', { count: low.length })}
            </Button>
          </div>
        )}
      </div>
      {canChange && choosable.length > 0 && (
        <div className={process.cardFooter}>
          {selected.length === 0 ? (
            <Paragraph data-size="sm" className={process.subtle}>
              {t('workEnvironmentPlan.choose.pick')}
            </Paragraph>
          ) : (
            <>
              {hasPlan(areas) && (
                <Alert data-color="info" data-size="sm">
                  <Paragraph data-size="sm">{t('workEnvironmentPlan.choose.oneIsEnough')}</Paragraph>
                </Alert>
              )}
              <div>
                <Button
                  variant="primary"
                  data-size="sm"
                  onClick={() => onCreate(selected.map((area) => area.key))}
                >
                  {selected.length === 1
                    ? t('workEnvironmentPlan.choose.create', { name: areaName(selected[0], t).toLowerCase() })
                    : t('workEnvironmentPlan.choose.createMany', { count: selected.length })}
                </Button>
              </div>
              <Paragraph data-size="sm" className={process.subtle}>
                {t('workEnvironmentPlan.choose.next')}
              </Paragraph>
            </>
          )}
        </div>
      )}
    </section>
  );
};
